import React, { useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Icon from '@/components/Icon';
import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import Text from '@/components/Typography/Text';
import FlowField from '@/components/Landing/FlowField';
import LandingClosing from '@/components/Landing/LandingClosing';
import Reveal from '@/components/Landing/Reveal';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import {
  Accent,
  BleedRule,
  Chip,
  CtaRow,
  DisplayHeading,
  Figure,
  GhostCta,
  Kicker,
  Lede,
  Marquee,
  PrimaryCta,
  Section,
  Split,
  useEntrance,
  useLanding,
} from '@/components/Landing/LandingKit';
import {
  ACCENT,
  LAYOUT,
  fluid,
  isWeb,
  labelType,
} from '@/components/Landing/landingTokens';
import { isDarkTheme } from '@/constants/Colors';
import { useTheme } from '@/context/ThemeContext';
import {
  FREE_USER_STARTING_CREDITS_DISPLAY,
  GAB_AI_SOFTWARE_ID,
  PLUS_MONTHLY_CREDITS_DISPLAY,
} from '@/constants/config';

const SITE = 'https://gab.ai';

/* ------------------------------------------------------------------ */
/* Content                                                             */
/* ------------------------------------------------------------------ */

const MARQUEE = [
  'Free to start', 'No card', 'Every model', 'Images', 'Video', 'Music',
  'Voice', 'Web search', 'Files', 'Code', 'Agents', 'Prompts', 'Explore',
];

/**
 * What a free account can actually do. Every line here has to stay true of the
 * free tier specifically — this is the page people cite when they feel misled.
 */
const FREE_INCLUDES = [
  'Chat with every model on the platform',
  'Arya — unfiltered answers from our own model',
  'Automatic routing to the right model',
  'Image generation and editing',
  'Video and music generation',
  'Voice conversations',
  'Web search and research',
  'File and PDF analysis',
  'One custom agent',
  'The prompt library and Explore',
  'iPhone, iPad, Android, and Mac apps',
  'Full export of everything you make',
];

/** Answers the "so what's the catch" question before it gets asked. */
const CREDIT_NOTES = [
  {
    icon: 'coins',
    title: 'One balance, every medium',
    copy: 'Credits are the only currency. There is no image plan, no video plan, and no separate bill per capability — a picture and a paragraph come out of the same pool.',
  },
  {
    icon: 'gauge-high',
    title: 'A chat message costs one credit',
    copy: 'Messages to the default model are a single credit each. Premium models, image, video, and music generation cost more, scaled to what the underlying model charges.',
  },
  {
    icon: 'credit-card',
    title: 'Nothing expires into a bill',
    copy: 'Running out of credits stops generation until you top up or upgrade. It never charges a card you did not enter, and there is no trial that quietly converts.',
  },
];

const PLANS = [
  {
    key: 'free',
    kicker: 'Free',
    name: 'Start here',
    copy: 'Everything above, on the credits you get for signing up. Enough to properly test the models against whatever you use today.',
    points: [
      `${FREE_USER_STARTING_CREDITS_DISPLAY} credits at sign-up`,
      'No credit card, ever',
      'Every model and every medium',
      'One custom agent',
    ],
    cta: 'Create a free account',
    href: '/signup',
  },
  {
    key: 'plus',
    kicker: 'Plus',
    name: 'When free runs short',
    copy: 'For people who stopped rationing. Free messages on the default model, a monthly credit allowance for everything else, and no cap on agents or history.',
    points: [
      'Free default-model messages',
      `${PLUS_MONTHLY_CREDITS_DISPLAY} credits every month`,
      'Memory across conversations',
      'Unlimited agents and history',
    ],
    cta: 'See what Plus costs',
    href: '/pricing',
    accent: true,
  },
];

const FAQS = [
  {
    q: 'Is Gab AI really free?',
    a: `Yes. Creating an account gives you ${FREE_USER_STARTING_CREDITS_DISPLAY} credits and asks for no payment details. Those credits work on every model and every capability — chat, images, video, music, voice, and file analysis — rather than unlocking a chat-only demo.`,
  },
  {
    q: 'Do I need a credit card to use free AI chat?',
    a: 'No. There is no card on file, no trial period counting down, and nothing that converts to a paid plan on its own. If you want more credits later you choose to buy them.',
  },
  {
    q: 'Is there free AI chat with no sign up?',
    a: 'An account is required. Conversations, memory, files, and your credit balance are tied to it, and a signed-in account is also what lets us mask your identity from the third-party model providers behind a request. Signing up takes an email address and nothing else.',
  },
  {
    q: 'What happens when I run out of credits?',
    a: 'Generation pauses and your history stays exactly where it is. You can buy a credit pack outright, move to Plus for Work mode, a monthly allowance, and free messages on the default model, or simply wait — nothing is deleted and no card is charged.',
  },
  {
    q: 'Is free AI chat unlimited?',
    a: 'Not on the free tier — the starting credits are finite, which is what keeps the tier honest rather than rate-limited into uselessness. Plus includes Work mode and unlimited messages on the default model, so that is the tier to look at for agentic jobs and everyday chat with no counter.',
  },
  {
    q: 'Can I use the free AI chat on my phone?',
    a: 'Yes. The iPhone, iPad, Android, and Mac apps are free to install and use the same free account, and everything stays in sync with the web.',
  },
];

/* ------------------------------------------------------------------ */
/* Pieces                                                              */
/* ------------------------------------------------------------------ */

/** One line of the free-tier checklist. */
function IncludedRow({ label }) {
  const { band, isCompact } = useLanding();

  return (
    <View
      style={{
        flexGrow: 1,
        flexBasis: isCompact ? '100%' : '46%',
        minWidth: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 11,
        paddingVertical: 13,
        paddingRight: 16,
        borderBottomWidth: 1,
        borderBottomColor: band.rule,
      }}
    >
      <Icon name="check" size={10} color={ACCENT} solid />
      <Text style={{ color: band.inkSoft, flex: 1, minWidth: 0, fontSize: 13.5 }}>{label}</Text>
    </View>
  );
}

/** One "how credits work" note. */
function CreditNote({ note, isLast }) {
  const { c, width } = useLanding();

  return (
    <View
      style={{
        flexDirection: 'row',
        gap: 14,
        paddingVertical: fluid(width, 16, 20),
        borderBottomWidth: isLast ? 0 : 1,
        borderBottomColor: c.rule,
      }}
    >
      <View
        style={{
          width: 34,
          height: 34,
          borderRadius: 11,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: c.accentSoft,
        }}
      >
        <Icon name={note.icon} size={13} color={ACCENT} solid />
      </View>
      <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
        <Text weight="bold" style={{ color: c.ink, fontSize: 15, letterSpacing: -0.15 }}>
          {note.title}
        </Text>
        <Text style={{ color: c.muted, fontSize: 13.5, lineHeight: 21 }}>{note.copy}</Text>
      </View>
    </View>
  );
}

/** Free / Plus card. */
function PlanCard({ plan }) {
  const { c, isXS, isCompact, display } = useLanding();
  const h3 = display({ min: 22, max: 30, leading: 1.12, tracking: -0.032 });

  return (
    <View
      style={{
        flexGrow: 1,
        flexBasis: isCompact ? '100%' : '46%',
        minWidth: 0,
        borderRadius: 22,
        borderWidth: 1,
        borderColor: plan.accent ? 'rgba(13,92,71,0.22)' : c.rule,
        backgroundColor: plan.accent ? c.accentSoft : c.cardSolid,
        padding: isXS ? 22 : 28,
        gap: isXS ? 20 : 26,
        justifyContent: 'space-between',
      }}
    >
      <View style={{ gap: 12 }}>
        <Text weight="bold" style={{ color: plan.accent ? ACCENT : c.faint, ...labelType(9.5, 0.2) }}>
          {plan.kicker}
        </Text>
        <DisplayHeading tag="h3" mask={false} type={h3} lines={[[plan.name]]} />
        <Text style={{ color: c.muted, fontSize: 14, lineHeight: 22, maxWidth: 440 }}>
          {plan.copy}
        </Text>
      </View>

      <View style={{ gap: 16 }}>
        <View style={{ gap: 9 }}>
          {plan.points.map((point) => (
            <View key={point} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Icon name="circle-check" size={12} color={ACCENT} solid />
              <Text style={{ color: c.inkSoft, flex: 1, minWidth: 0, fontSize: 13.5 }}>{point}</Text>
            </View>
          ))}
        </View>
        <GhostCta label={plan.cta} href={plan.href} style={{ height: 34 }} />
      </View>
    </View>
  );
}

/* ------------------------------------------------------------------ */

export default function FreeAiChatPage() {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const insets = useSafeAreaInsets();
  const { c, band, isXS, isCompact, width, pad, display } = useLanding();
  const entered = useEntrance();

  useLandingStyles();

  const headline = display({ min: 38, max: 78, leading: 0.96, tracking: -0.042 });
  const h2 = display({ min: 30, max: 52, leading: 1.02, tracking: -0.038 });

  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE}/free-ai-chat#page`,
        url: `${SITE}/free-ai-chat`,
        name: 'Free AI chat',
        description: `Free AI chat with every major model. ${FREE_USER_STARTING_CREDITS_DISPLAY} credits at sign-up, no credit card, and images, video, music, and voice included.`,
        isPartOf: { '@id': GAB_AI_SOFTWARE_ID },
        breadcrumb: { '@id': `${SITE}/free-ai-chat#breadcrumb` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${SITE}/free-ai-chat#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Gab AI', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Free AI chat', item: `${SITE}/free-ai-chat` },
        ],
      },
      {
        '@type': 'Offer',
        '@id': `${SITE}/free-ai-chat#offer`,
        name: 'Gab AI free account',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        description: `${FREE_USER_STARTING_CREDITS_DISPLAY} credits at sign-up with no credit card required.`,
        itemOffered: { '@id': GAB_AI_SOFTWARE_ID },
      },
      {
        '@type': 'ItemList',
        '@id': `${SITE}/free-ai-chat#included`,
        name: 'Included with a free Gab AI account',
        itemListElement: FREE_INCLUDES.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item,
        })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE}/free-ai-chat#faq`,
        mainEntity: FAQS.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  }), []);

  return (
    <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
      <FlowField isDark={isDark} />
      <MetaHead
        title="Free AI Chat — Every Major Model, No Credit Card"
        description={`Free AI chat with GPT, Claude, Gemini, and Arya. ${FREE_USER_STARTING_CREDITS_DISPLAY} credits at sign-up, no credit card, and images, video, music, and voice included.`}
        canonical="/free-ai-chat"
        keywords="free AI chat, free AI chatbot, free AI chat online, AI chat free, free chat bot, free AI assistant, free AI to use, chat with AI free"
        structuredData={structuredData}
      />

      <ScrollView
        {...(isWeb ? { dataSet: { landingScroll: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole="main"
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        {/* ============ MASTHEAD ============ */}
        <View
          accessibilityRole={isWeb ? 'region' : undefined}
          accessibilityLabel="Free AI chat on Gab AI"
          style={{
            width: '100%',
            maxWidth: LAYOUT.max,
            paddingHorizontal: pad,
            paddingTop: (isXS ? insets.top : 0) + fluid(width, 104, 152),
            paddingBottom: fluid(width, 40, 60),
          }}
        >
          <View
            style={isWeb ? {
              opacity: entered ? 1 : 0,
              transition: 'opacity 700ms ease 120ms',
            } : null}
          >
            <Kicker>Free to start</Kicker>
          </View>

          <DisplayHeading
            tag="h1"
            entered={entered}
            type={headline}
            style={{ marginTop: fluid(width, 22, 32) }}
            lines={[
              ['Free AI chat,'],
              ['without the ', <Accent key="a" size={headline.fontSize} leading={headline.lineHeight}>asterisk</Accent>, '.'],
            ]}
          />

          <View style={{ height: 1, backgroundColor: c.rule, marginTop: fluid(width, 30, 48) }} />

          <View
            style={{
              flexDirection: isCompact ? 'column' : 'row',
              gap: isCompact ? 30 : 56,
              alignItems: isCompact ? 'stretch' : 'flex-start',
              paddingTop: fluid(width, 26, 40),
              ...(isWeb ? {
                opacity: entered ? 1 : 0,
                transform: [{ translateY: entered ? 0 : 18 }],
                transition: 'opacity 800ms ease 420ms, transform 800ms ease 420ms',
              } : {}),
            }}
          >
            <View style={{ flex: isCompact ? undefined : 1.15, minWidth: 0 }}>
              <Lede max={480}>
                Most free AI chat is one weak model, a message counter, and a card
                form at the end. This one hands you credits that spend on every model
                and every medium — GPT, Claude, Gemini, and Arya, plus images,
                video, music, and voice — and never asks for payment details.
              </Lede>
            </View>

            <View style={{ flex: isCompact ? undefined : 1, minWidth: 0, gap: 18 }}>
              <CtaRow>
                <PrimaryCta label="Start chatting free" full={isXS} />
                <GhostCta label="Compare Free and Plus" href="/pricing" />
              </CtaRow>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 }}>
                <Chip icon="check">No credit card</Chip>
                <Chip icon="check">No trial timer</Chip>
                <Chip icon="check">Cancel nothing</Chip>
              </View>
            </View>
          </View>
        </View>

        <BleedRule />
        <Marquee items={MARQUEE} />
        <BleedRule />

        {/* ============ WHAT FREE INCLUDES (band) ============ */}
        <View
          style={{
            width: '100%',
            alignSelf: 'stretch',
            alignItems: 'center',
            backgroundColor: band.bg,
            overflow: 'hidden',
            borderTopWidth: 1,
            borderBottomWidth: 1,
            borderColor: band.rule,
          }}
        >
          {isWeb ? (
            <View
              aria-hidden
              pointerEvents="none"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(ellipse 46% 62% at 14% 30%, ${band.glow}, transparent 68%)`,
              }}
            />
          ) : null}

          <Section label="What a free account includes" background="transparent">
            <Split ratio={[1, 1.25]} align="flex-start">
              <Reveal>
                <Kicker tone="band">The whole product</Kicker>
                <DisplayHeading
                  tag="h2"
                  mask={false}
                  type={h2}
                  color={band.ink}
                  style={{ marginTop: 22 }}
                  lines={[['Free is not a'], ['smaller product.']]}
                />
                <Lede tone="band" max={400} style={{ marginTop: 22 }}>
                  Nothing on this list is held back for paying accounts. The
                  difference between Free and Plus is how much you can run, not what
                  you are allowed to touch.
                </Lede>

                <View
                  style={{
                    marginTop: fluid(width, 26, 34),
                    padding: 22,
                    borderRadius: 16,
                    borderWidth: 1,
                    borderColor: band.rule,
                    backgroundColor: band.surfaceSoft,
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 28,
                  }}
                >
                  <Figure
                    size="sm"
                    tone="band"
                    accent
                    value={FREE_USER_STARTING_CREDITS_DISPLAY}
                    label="credits at sign-up"
                  />
                  <Figure size="sm" tone="band" value="$0" label="to create an account" />
                  <Figure size="sm" tone="band" value="0" label="card details asked for" />
                </View>
              </Reveal>

              <Reveal delay={120}>
                <View
                  style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    borderTopWidth: 1,
                    borderTopColor: band.rule,
                  }}
                >
                  {FREE_INCLUDES.map((item) => (
                    <IncludedRow key={item} label={item} />
                  ))}
                </View>
              </Reveal>
            </Split>
          </Section>
        </View>

        {/* ============ HOW CREDITS WORK ============ */}
        <Section label="How free credits work">
          <Split ratio={[1, 1.15]} align="flex-start">
            <Reveal>
              <Kicker>The catch, such as it is</Kicker>
              <DisplayHeading
                tag="h2"
                mask={false}
                type={h2}
                style={{ marginTop: 22 }}
                lines={[['Credits, and'], ['nothing else.']]}
              />
              <Lede max={400} style={{ marginTop: 20 }}>
                There is one meter instead of a pricing page full of them. Here is
                exactly how it moves, so the first surprise is not an empty balance.
              </Lede>
              <GhostCta label="Read the credits guide" href="/docs/credits" style={{ marginTop: 10 }} />
            </Reveal>

            <Reveal delay={120}>
              <View
                style={{
                  borderRadius: 18,
                  borderWidth: 1,
                  borderColor: c.rule,
                  backgroundColor: c.cardSolid,
                  paddingHorizontal: isXS ? 18 : 24,
                  paddingVertical: 6,
                }}
              >
                {CREDIT_NOTES.map((note, index) => (
                  <CreditNote
                    key={note.title}
                    note={note}
                    isLast={index === CREDIT_NOTES.length - 1}
                  />
                ))}
              </View>
            </Reveal>
          </Split>
        </Section>

        <BleedRule />

        {/* ============ FREE VS PLUS ============ */}
        <Section label="Free compared with Plus">
          <Reveal>
            <Kicker>Two tiers</Kicker>
            <DisplayHeading
              tag="h2"
              mask={false}
              type={h2}
              style={{ marginTop: 22 }}
              lines={[['Stay free, or'], ['stop counting.']]}
            />
            <Lede max={520} style={{ marginTop: 20 }}>
              Plenty of people never leave the free tier, and that is a perfectly
              good outcome. Plus exists for the point where watching a balance
              starts costing more attention than it saves.
            </Lede>
          </Reveal>

          <Reveal delay={100}>
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: isXS ? 12 : 16,
                marginTop: fluid(width, 36, 56),
              }}
            >
              {PLANS.map((plan) => (
                <PlanCard key={plan.key} plan={plan} />
              ))}
            </View>
          </Reveal>
        </Section>

        {/* ============ FAQ ============ */}
        <Section label="Free AI chat questions">
          <Split ratio={[1, 1.5]} align="flex-start">
            <Reveal>
              <Kicker>Answers</Kicker>
              <DisplayHeading
                tag="h2"
                mask={false}
                type={h2}
                style={{ marginTop: 22 }}
                lines={[['What “free”'], ['actually means.']]}
              />
              <Lede size="sm" max={340} style={{ marginTop: 20 }}>
                The questions worth answering before you hand any product an email
                address.
              </Lede>
              <GhostCta label="See the full pricing page" href="/pricing" style={{ marginTop: 12 }} />
            </Reveal>

            <Reveal delay={100}>
              <View style={{ borderTopWidth: 1, borderTopColor: c.rule }}>
                {FAQS.map((item) => (
                  <View
                    key={item.q}
                    style={{
                      paddingVertical: fluid(width, 18, 24),
                      borderBottomWidth: 1,
                      borderBottomColor: c.rule,
                      gap: 8,
                    }}
                  >
                    <Text
                      tag="h3"
                      weight="bold"
                      style={{ color: c.ink, fontSize: 16.5, letterSpacing: -0.2, lineHeight: 24 }}
                    >
                      {item.q}
                    </Text>
                    <Text tag="p" style={{ color: c.muted, fontSize: 14, lineHeight: 22, maxWidth: 620 }}>
                      {item.a}
                    </Text>
                  </View>
                ))}
              </View>
            </Reveal>
          </Split>
        </Section>

        <LandingClosing />
        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
